import { Mutation } from "@apollo/client/react/components";
import LocaleContext from "components/i18n/LocaleContext";
import RatingDistribution from "components/rating/RatingDistribution";
import RatingStars from "components/rating/RatingStars";
import TotalRatingsLine from "components/rating/TotalRatingsLine";
import classNames from "components/ui/classNames";
import differenceInMonths from "date-fns/differenceInMonths";
import lightFormat from "date-fns/lightFormat";
import parseISO from "date-fns/parseISO";
import RankedIssuerFields from "lib/fragments/RankedIssuerFields";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import type { IBrazilianRealEstateFundTopLevelFragment } from "lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import type { IRankedIssuerFieldsFragment } from "lib/fragments/__generated__/RankedIssuerFields.generated";
import useSelfie from "lib/hooks/useSelfie";
import RateIssuer from "lib/queries/RateIssuer";
import type { IRateIssuerMutation, IRateIssuerMutationVariables } from "lib/queries/__generated__/RateIssuer.generated";
import getNumberWithOrdinal from "lib/utils/getOrdinalNumber";
import parseDateYmd from "lib/utils/parseDateYmd";
import { BellRingIcon } from "lucide-react";
import { useContext } from "react";
import type { IBrazilianRealEstateFundIssuer } from "types/graphqlTypes";
import { IRankingIssuerType } from "types/graphqlTypes";

interface IRatingProps {
  issuer:
    | IBrazilianCompanyIssuerTopLevelFragment
    | IBrazilianRealEstateFundTopLevelFragment
    | IBrazilianRealEstateFundIssuer
    | IAmericanCompanyIssuerTopLevelFragment;
  rankingPlacement: IRankedIssuerFieldsFragment["overallRankingPlacement"];
  rankingRatingSummary: IRankedIssuerFieldsFragment["overallRankingRatingSummary"];
}

function Rating({ issuer, rankingPlacement, rankingRatingSummary }: IRatingProps) {
  const selfie = useSelfie();
  const overallRating = rankingRatingSummary ? rankingRatingSummary.overallRating : 0;
  const locale = useContext(LocaleContext);

  return (
    <Mutation<IRateIssuerMutation, IRateIssuerMutationVariables> mutation={RateIssuer}>
      {(rateIssuer, { error, loading }) => (
        <div className="space-y-2">
          <div className="flex flex-col space-y-2 py-2 xl:flex-row xl:space-x-8 xl:space-y-0">
            <div className="space-y-4">
              <div className="flex flex-col items-center space-y-4 xl:flex-row xl:space-x-4 xl:space-y-0">
                <h3 className="p-4 pb-0 text-center font-aeonik text-4xl font-semibold xl:p-0 xl:text-left xl:text-3xl">
                  {overallRating} / 5
                </h3>

                {rankingPlacement && selfie && selfie.isPremium && (
                  <strong className="inline-flex rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-gray-700">
                    {locale === "BRA"
                      ? `${rankingPlacement.position}º no ranking`
                      : `${getNumberWithOrdinal(rankingPlacement.position)} position`}
                  </strong>
                )}
              </div>

              <div>
                <RatingStars
                  rating={issuer.rating?.value || 0}
                  isSubmittingRating={loading}
                  onSubmitRating={(rating) => {
                    if (issuer.__typename === "BrazilianRealEstateFundIssuer") {
                      return;
                    }

                    let issuerType = IRankingIssuerType.AmericanCompany;

                    if (issuer.__typename === "BrazilianCompanyIssuer") {
                      issuerType = IRankingIssuerType.BrazilianCompany;
                    }
                    if (issuer.__typename === "BrazilianRealEstateFund") {
                      issuerType = IRankingIssuerType.BrazilianRealEstateFund;
                    }

                    if (rating === issuer.rating?.value) {
                      rateIssuer({
                        variables: {
                          input: {
                            issuerId: issuer._id,
                            issuerType,
                            rating: 0,
                          },
                        },
                        update: (cache) => {
                          const data = cache.readFragment<IRankedIssuerFieldsFragment>({
                            fragment: RankedIssuerFields,
                            id: cache.identify(issuer),
                          });
                          if (!data) {
                            return;
                          }
                          cache.writeFragment({
                            fragment: RankedIssuerFields,
                            id: cache.identify(issuer),
                            data: {
                              ...data,
                              rating: null,
                            },
                          });
                        },
                      });
                    }

                    if (rating === issuer.rating?.value) {
                      return;
                    }

                    rateIssuer({
                      variables: {
                        input: {
                          issuerId: issuer._id,
                          issuerType,
                          rating,
                        },
                      },
                      update: (cache) => {
                        const data = cache.readFragment<IRankedIssuerFieldsFragment>({
                          fragment: RankedIssuerFields,
                          id: cache.identify(issuer),
                        });
                        if (!data) {
                          return;
                        }
                        cache.writeFragment({
                          fragment: RankedIssuerFields,
                          id: cache.identify(issuer),
                          data: {
                            ...data,
                            rating: {
                              ...data.rating,
                              value: rating,
                              ratedAt: new Date().toISOString(),
                            },
                          },
                        });
                      },
                    });
                  }}
                />
              </div>

              {rankingRatingSummary && (
                <TotalRatingsLine
                  totalRatings={rankingRatingSummary.totalRatings}
                  periodStart={parseDateYmd(rankingRatingSummary.periodStartDate)}
                  periodEnd={parseDateYmd(rankingRatingSummary.periodEndDate)}
                />
              )}
            </div>

            {rankingRatingSummary && rankingRatingSummary.distribution.length > 0 && (
              <div>
                <RatingDistribution distribution={rankingRatingSummary.distribution} />
              </div>
            )}
          </div>

          {issuer.rating && differenceInMonths(new Date(), parseISO(issuer.rating.ratedAt)) >= 3 && (
            <div className="flex items-center justify-center space-x-2 text-orange-500 xl:justify-start">
              <span
                className={classNames(
                  "rounded-xl text-center",
                  "bg-orange-50 px-3 py-2 text-xs font-normal xl:text-sm",
                )}
              >
                {"Sua última avaliação foi em "}
                <strong>{lightFormat(parseISO(issuer.rating.ratedAt), "dd/MM/yyyy")}</strong>
              </span>
              <BellRingIcon className="hidden h-4 w-4 xl:block" strokeWidth={2} />
            </div>
          )}

          {error && (
            <div>
              <div
                className={classNames(
                  "inline-flex w-full justify-center rounded-xl bg-rose-50 px-3 py-2 text-center",
                  "text-sm font-normal text-red-600 xl:w-auto xl:max-w-xs xl:justify-start",
                )}
              >
                Não foi possível enviar sua nota...
              </div>
            </div>
          )}
        </div>
      )}
    </Mutation>
  );
}

export default Rating;
